import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteContext from 'site/SiteContext';

import { Body, Box, breakpoints, Card, SearchResultsGrid } from 'dma-ui-kit';
import { translateInterfaceText } from 'helpers/translation';
import ImageNotFound from 'assets/resultmissing.png';

import { fromUnixTime } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { emptyUpcomingResults, fetchUpcomingResults } from 'search/upcomingSearch';

import styled from 'styled-components';
import useBreakpoint from 'use-breakpoint';

const NoLink = styled(Box)`
    padding: 0 10px;
`;

const EventLink = styled(Link)`
    padding: 0 10px;
`;

const UpcomingEvents = React.memo((props) => {
    const { displayLanguage } = useContext(SiteContext);
    const searchEndpoint = `${process.env.REACT_APP_SITESEARCH_BASE_PATH}api/search/upcoming`;
    const [results, setResults] = useState(emptyUpcomingResults);

    const { attributes, relationships } = props.paragraph;
    const { data } = relationships.field_event_category;
    const limit = attributes.field_display_limit ? attributes.field_display_limit : 10;
    const categories = data.map(item => item.attributes.drupal_internal__tid);
    const categoryList = categories.join(' OR ');

    const date = new Date();
    const dateAfter = new Date(date.getFullYear(), date.getMonth(), date.getDate()).toISOString().slice(0, 10);

    const options = useMemo(() => ({
        page: 1,
        limit,
        filters: {
          categoryid: categoryList ? [categoryList] : [],
          dateAfter: [dateAfter],
        }
      }), [limit, categoryList, dateAfter]);

    const labels = {
      noUpcomingEvents: translateInterfaceText("No upcoming events")
    };

    const { breakpoint } = useBreakpoint(breakpoints);
    const getTilesPerRow = () => {
      switch(breakpoint) {
        case "mobile":
          return 1;
        case "tablet":
        case "tabletSmall":
          return 2;
        default:
          return 4;
      };
    };


    useEffect(() => {
      let sectionActive = true;

      const fetchResults = async () => {
        const nextResults = await fetchUpcomingResults(searchEndpoint, options, displayLanguage);

        if (sectionActive) {
          setResults(nextResults);
        }
      };

      fetchResults();

      return () => {
        sectionActive = false;
      };
    }, [searchEndpoint, options, displayLanguage]);

    const formatEventDate = (result) => {
      if (!result?.field_date_rrule || !result?.date_rrule_end) return null;

      const startDay = formatInTimeZone(fromUnixTime(result.field_date_rrule), 'America/Chicago', 'MMM do yyyy');
      const endDay = formatInTimeZone(fromUnixTime(result.date_rrule_end), 'America/Chicago', 'MMM do yyyy');
      const startTime = formatInTimeZone(fromUnixTime(result.field_date_rrule), 'America/Chicago', 'h:mm aaa');
      const endTime = formatInTimeZone(fromUnixTime(result.date_rrule_end), 'America/Chicago', 'h:mm aaa');

      return (
        <Body variant='small'>
          {startDay}
          {(startDay !== endDay) && (<> - {endDay}</>)}
          <br/>
          {startTime} - {endTime}
        </Body>
      );
    };

    return (
      <Box w="80%" margin="auto" center="center">
        <SearchResultsGrid cardsPerRow={getTilesPerRow()}>
          {(results?.hits?.length > 0) ? 
            results.hits.map((result, index) => (
              <EventLink key={result.id || index} to={{ pathname: result.url }}>
                <Card 
                    image={result.thumbnail || result.featured_image || ImageNotFound}
                    title={result.title}
                    alt={result.featured_image_alt || result.title}
                    caption={formatEventDate(result) || ''}
                    variant="mini"
                />
              </EventLink>
            )) : (
              <NoLink>
                  <Card 
                      image={ImageNotFound }
                      title={labels.noUpcomingEvents}
                      alt={labels.noUpcomingEvents}
                      caption={''}
                      variant="mini"
                  />
              </NoLink>
            )}
        </SearchResultsGrid>
      </Box>
    );
});

export default UpcomingEvents;